import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  GoogleMap,
  LoadScript,
  Marker,
  Autocomplete
} from '@react-google-maps/api';
import { Box, TextField, Typography, IconButton, InputAdornment } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

export interface LatLng {
  lat: number;
  lng: number;
}

interface MapSelectorProps {
  onLocationSelect: (location: LatLng & { address: string }) => void;
  /** Called when the user clears the search / pin (X). Parent should reset stored lat/lng. */
  onLocationClear?: () => void;
  defaultCenter?: LatLng;
  height?: string | number;
  initialLocation?: LatLng & { address: string };
}

const DEFAULT_CENTER: LatLng = { lat: 28.6139, lng: 77.2090 };
const DEFAULT_HEIGHT = '400px';

// Helper function to check if a string is a Plus Code
const isPlusCode = (address: string): boolean => {
  const plusCodePattern = /^[A-Z0-9]{2,}\+[A-Z0-9]{2,}$/i;
  return plusCodePattern.test(address.trim());
};

// Helper function to remove Plus Code from an address string
const removePlusCode = (address: string): string => {
  // Pattern to match Plus Code at the start of string (with optional comma/space after)
  const plusCodePattern = /^[A-Z0-9]{2,}\+[A-Z0-9]{2,}[\s,]*/i;
  let cleaned = address.replace(plusCodePattern, '').trim();
  
  // Also remove Plus Code if it appears anywhere in the string (with commas/spaces around it)
  cleaned = cleaned.replace(/[\s,]*[A-Z0-9]{2,}\+[A-Z0-9]{2,}[\s,]*/gi, ' ').trim();
  
  // Clean up multiple spaces or commas
  cleaned = cleaned.replace(/[\s,]+/g, ' ').replace(/^[\s,]+|[\s,]+$/g, '');
  
  return cleaned || address; // Return original if cleaning removes everything
};

// Helper function to extract a proper address from geocoding results
const extractAddressFromResults = (results: google.maps.GeocoderResult[]): string => {
  // First, try to find a result with a proper formatted address (not a Plus Code)
  for (const result of results) {
    if (result.formatted_address) {
      const address = result.formatted_address;
      // If it's not just a Plus Code, clean it and return
      if (!isPlusCode(address)) {
        return removePlusCode(address);
      }
    }
  }

  // If all formatted_addresses are Plus Codes, try to use compound_code from plus_code
  for (const result of results) {
    if (result.plus_code?.compound_code) {
      const parts = result.plus_code.compound_code.split(',');
      if (parts.length > 1) {
        // Return location info without the Plus Code
        const address = parts.slice(1).join(',').trim();
        return removePlusCode(address);
      }
    }
  }

  // Fallback: clean the first result's formatted_address
  const fallback = results[0]?.formatted_address || 'Unknown Location';
  return removePlusCode(fallback);
};

const MapSelector: React.FC<MapSelectorProps> = ({
  onLocationSelect,
  onLocationClear,
  defaultCenter = DEFAULT_CENTER,
  height = DEFAULT_HEIGHT,
  initialLocation,
}) => {
  const [selectedPosition, setSelectedPosition] = useState<LatLng | null>(initialLocation || null);
  const [address, setAddress] = useState(initialLocation?.address || '');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update map when initialLocation changes (e.g., when loading from draft)
  useEffect(() => {
    if (initialLocation) {
      setSelectedPosition(initialLocation);
      setAddress(initialLocation.address);
      if (inputRef.current) {
        inputRef.current.value = initialLocation.address;
      }
      if (mapRef.current) {
        mapRef.current.panTo(initialLocation);
      }
    }
  }, [initialLocation]);

  // ✖️ Clear text field and selection
  const handleClear = () => {
    setAddress('');
    setSelectedPosition(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onLocationClear?.();
  };

  // 🔄 Update on place search
  const handlePlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place.geometry?.location) return;

    const latLng = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    const formattedAddress = place.formatted_address || place.name || '';

    setSelectedPosition(latLng);
    setAddress(formattedAddress);
    mapRef.current?.panTo(latLng);
    onLocationSelect({ ...latLng, address: formattedAddress });
  }, [onLocationSelect]);

  // 📍 Update on map click (with reverse geocoding)
  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const latLng = { lat, lng };

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const selectedAddress = extractAddressFromResults(results);
          setAddress(selectedAddress);
          if (inputRef.current) {
            inputRef.current.value = selectedAddress;
          }
          onLocationSelect({ ...latLng, address: selectedAddress });
        }
      });

      setSelectedPosition(latLng);
    },
    [onLocationSelect]
  );

  // 📌 Fetch and set current location
  const handleCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latLng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // Reverse-geocode to get a human-readable address (optional)
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: latLng }, (results, status) => {
          const selectedAddress =
            status === 'OK' && results && results.length > 0
              ? extractAddressFromResults(results)
              : 'Current Location';

          setSelectedPosition(latLng);
          setAddress(selectedAddress);
          if (inputRef.current) {
            inputRef.current.value = selectedAddress;
          }
          mapRef.current?.panTo(latLng);
          onLocationSelect({ ...latLng, address: selectedAddress });
        });
      },
      (error) => {
        console.error('Error fetching current location:', error);
      },
      { enableHighAccuracy: true }
    );
  }, [onLocationSelect]);

  return (
        <>
      <Box mb={2} sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#ECE0DA',
          borderRadius: '32px',
        },
      }}>
        <Autocomplete
          onLoad={(auto) => (autocompleteRef.current = auto)}
          onPlaceChanged={handlePlaceChanged}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            variant="outlined"
            placeholder="Search for a location"
            size="small"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClear} size="small">
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Autocomplete>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height }}
          center={selectedPosition ?? defaultCenter}
          zoom={selectedPosition ? 15 : 5}
          onClick={handleMapClick}
          onLoad={(map) => {
            mapRef.current = map;
            console.log('map', map);
          }}
          options={{ disableDefaultUI: true, zoomControl: true }}
        >
          {selectedPosition && <Marker position={selectedPosition} />}
        </GoogleMap>

        {/* 🔘 Current Location Button */}
        <IconButton
          aria-label="current location"
          onClick={handleCurrentLocation}
          sx={{
            position: 'absolute',
            bottom: 120,
            right: 8,
            backgroundColor: 'white',
            boxShadow: 1,
            '&:hover': { backgroundColor: 'grey.100' },
          }}
        >
          <MyLocationIcon />
        </IconButton>
      </Box>

      {/* {selectedPosition && (
        <Typography mt={2}>
          <strong>Lat:</strong> {selectedPosition.lat.toFixed(6)}<br />
          <strong>Lng:</strong> {selectedPosition.lng.toFixed(6)}<br />
          <strong>Address:</strong> {address}
        </Typography>
      )} */}
    </>
  );
};

export default MapSelector;
